/* eslint-disable react/require-default-props */
// SmallMultiplesScatter
//
// The faceted ("small multiples") rendering of the scatter plot — the 2D
// realization of facet_by, sibling to PrototypeScatterPlot. Built as a single
// plotly figure with a grid of subplots: one panel per facet, ranges shared
// across panels via axis `matches`.
//
// It takes the SAME props as PrototypeScatterPlot (the `formattedData` lookup
// plus key names, colorMap, palette, handlers) and adds `facetKeys`
// (per-point facet identity — e.g. findCategoricalSlice(data,"expansion").
// values) and `facetOrder`. So the eventual wrapper dispatch is the existing
// prop block plus those two.
//
// Point encoding mirrors PrototypeScatterPlot: every trace carries the
// full-length arrays with non-member points nulled, and `selectedpoints` uses
// global indices. That keeps selection faceting-agnostic and makes
// plotly_click / plotly_selected report global indices with no remap. Color
// faceting comes from the shared getSolidColorGroups() seam.
//
// Deliberately deferred for this first cut (each a focused follow-up, flagged
// rather than half-built):
//   - Continuous color keeps no on-top re-sort: one colorscale trace per facet
//     in natural order. Drops the contTraceIndex remap (and the index-mangling
//     it caused); the cost is dense bins may overdraw sparse ones within a
//     panel, which faceting already mitigates.
//   - Regression lines (per-facet fit). Identity lines are now supported (see
//     the showIdentityLine prop): per-subplot shapes injected after autorange,
//     with a master-only autoscale hack propagated across panels via `matches`.
//   - The imperative handle's extended methods (annotateSelected / download
//     image). onLoad still fires with the single figure node.
import React, { useEffect, useRef, useState } from "react";
import type Plotly from "plotly.js";
import type {
  Config,
  Layout,
  PlotData,
  PlotlyHTMLElement,
  PlotMouseEvent,
  PlotSelectionEvent,
} from "plotly.js";
import { usePlotlyLoader } from "../../../../../contexts/PlotlyLoaderContext";
import { MAX_POINTS_TO_ANNOTATE } from "../../../../../constants/plotConstants";
import {
  AnnotationTail,
  applyAnnotationTailOverrides,
  applyLegendLabelOverrides,
  calcAnnotationArrowWidth,
  calcAnnotationPositions,
  calcAutoscaleShapes,
  calcChromeAxisOverrides,
  calcExtraLinesMargin,
  calcPlotIndicatorLineShapes,
  captureAnnotationTail,
  cloneFigureForExport,
  DataExplorerColorPalette,
  DEFAULT_CHROME_LINE_WIDTH,
  DEFAULT_DATA_LINE_WIDTH,
  DEFAULT_PALETTE,
  EXPORT_EDGE_PADDING,
  facetMaskFor,
  onWebglContextRestored,
  releaseWebglContexts,
  resolveAnnotationTail,
  getLegendTraces,
  getRange,
  legendLayoutFor,
  wantsLegendTraces,
  getSolidColorGroups,
  hexToRgba,
  LegendInfo,
  LegendKey,
  orderContinuousPointsByBin,
  facetLabelBudget,
  truncateFacetLabel,
  RegressionLine,
  widenExtentsToAxisRange,
} from "./plotUtils";
import usePlotResizer from "./usePlotResizer";
import type ExtendedPlotType from "../../../ExtendedPlotType";
import styles from "../../../styles/ScatterPlot.scss";

type Data = Record<string, any>;

interface Props {
  data: Data;
  xKey: string;
  yKey: string;
  xLabel: string;
  yLabel: string;
  // Per-point facet identity, length matches data[xKey]. For the expansion
  // case this is the per-point expansion label.
  facetKeys: string[];
  // Explicit facet identity/order; defaults to first-seen order of facetKeys.
  facetOrder?: string[];
  height: number | "auto";
  // Drives the dummy legend traces + `layout.legend` baked into a downloaded
  // image — see `downloadImage` below and PrototypeScatterPlot's identical
  // prop/mechanism. Omitted (or missing at download time): downloadImage
  // warns and exports a plain figure with no legend, same as
  // PrototypeScatterPlot's own fallback.
  legendForDownload?: LegendInfo;
  // Renders a Plotly-native legend (dummy traces built from legendForDownload,
  // same mechanism as downloadImage below) instead of DE2's own custom legend.
  // Off by default; used by embedded plots rendered outside Data Explorer,
  // which have no surrounding UI to host the custom legend. Mirrors
  // PrototypeScatterPlot's identical prop.
  showBuiltinLegend?: boolean;
  hoverTextKey?: string;
  annotationTextKey?: string;
  colorKey1?: string;
  colorKey2?: string;
  categoricalColorKey?: string;
  continuousColorKey?: string;
  // Per-point bin-key series for continuous color (LEGEND_RANGE_*), parallel to
  // the continuous values. Supplied by the caller (e.g. useScatterPlotData) so
  // continuous points can stack by bin like PrototypeScatterPlot.
  contLegendKeys?: LegendKey[] | null;
  colorMap: Map<LegendKey, string>;
  selectedPoints?: Set<number>;
  pointVisibility?: boolean[];
  onClickPoint?: (pointIndex: number, anyModifier: boolean) => void;
  onMultiselect?: (pointIndices: number[]) => void;
  onClickResetSelection?: () => void;
  onLoad?: (plot: ExtendedPlotType) => void;
  pointSize?: number;
  pointOpacity?: number;
  outlineWidth?: number;
  palette?: DataExplorerColorPalette;
  // Point labels. Their own setting rather than a ratio of the axis
  // font: these sit inside the plot area competing with the data.
  annotationFontSize?: number;
  xAxisFontSize?: number;
  yAxisFontSize?: number;
  // The points to put text labels on. Same annotation channel as the
  // single-panel plots: under facet_by === "expansion" this is the handful of
  // contacted points (one representative per selected model), NOT the
  // re-expanded `selectedPoints`, so labels stay sparse even tiled across
  // panels. Each lands on its own facet's axes. Falls back to `selectedPoints`
  // when omitted. The annotated-subset-of-selected invariant is enforced
  // upstream in useSelection's derivation, not here.
  pointsToAnnotate?: Set<number>;
  // The count shown in the over-cap fallback (selection.size under collapse,
  // threaded from the wrapper); defaults to the selected-point count.
  selectionCount?: number;
  // Draw the x = y identity line in every panel. Off by default (current
  // faceted behavior). When on, a master-only autoscale hack equalizes the x/y
  // scale and propagates to all panels via `matches`, so the line reads ~45°
  // everywhere; the line itself is injected per subplot after autorange.
  showIdentityLine?: boolean;
  // Per-facet regression lines keyed by facet label (from useScatterPlotData).
  // Each panel draws its own; a facet with no entry (or an empty one) draws
  // none. Usually one line per panel; several when the plot is configured to
  // fit one per color (show_regression_line_per_color).
  regressionLinesByFacet?: Map<string, RegressionLine[]> | null;
  // When true, a facet with no plottable point is kept as an empty panel
  // labeled "(no data)" rather than dropped. Used by expanded plots so a
  // paginated transcript window keeps its full set of panels even when the
  // dataset doesn't measure every transcript.
  placeholderEmptyFacets?: boolean;
  // Facets toggled off via the "Facets" panel (shown when color_by/facet_by
  // diverge). Unlike a facet hidden only by the color legend (which keeps its
  // panel, rendered empty — see the placeholderEmptyFacets comment above),
  // this is a deliberate, causal user action: the facet's panel is fully
  // removed from the grid, not grayed out.
  hiddenFacets?: Set<string>;
  // Transient view state to start from, instead of this instance building its
  // own up from scratch — see PrototypeScatterPlot's identical pair. Produce
  // these with `captureTransientState` against the live plot.
  initialAxes?: Partial<Layout>;
  initialAnnotationTails?: Record<string, AnnotationTail>;
}

type PlotlyType = typeof Plotly;
type PropsWithPlotly = Props & { Plotly: PlotlyType };

// Unlike PrototypeScatterPlot, which caches whole axis objects, this renderer
// caches only the master pair's range and rebuilds everything else (titles,
// tick fonts, `matches` links) per facet on every render. So a seed is stripped
// to the range: carrying a captured axis's `tickfont` in would override the
// font size this instance was asked to render with, which is exactly the thing
// an export preview exists to let someone change.
const narrowSeededAxes = (initialAxes: Partial<Layout> | undefined) => {
  if (!initialAxes) {
    return {};
  }

  const narrow = (axis: any) =>
    axis ? { range: axis.range, autorange: false } : undefined;

  return {
    xaxis: narrow(initialAxes.xaxis),
    yaxis: narrow(initialAxes.yaxis),
  };
};

// Fractions of the whole grid, tuned against the live plot's own default
// axis font size (14px) — see gapX/gapY below for why they can't stay fixed
// constants once the font size varies (a bigger export font, or the global
// setting itself).
const DEFAULT_GAP_X = 0.06;
const DEFAULT_GAP_Y = 0.1;
const DEFAULT_GAP_AXIS_FONT_SIZE = 14;

// Hoisted out of the layout literal because facetLabelBudget needs the
// horizontal pair: paper-referenced annotations span the figure minus these,
// not the figure.
const MARGIN = { t: 28, r: 16, b: 60, l: 78 };
// Facet titles follow the x-axis font size rather than a constant of their
// own, because a constant doesn't scale: an export at 2100 px kept 11 px
// panel titles and they became unreadable, while every other size in the
// figure could be adjusted.
//
// 0.8 is not arbitrary — it reproduces the 11 px this used to hardcode at the
// default 14 px axis font, so no existing plot changes appearance. It also
// flows into facetLabelBudget below, so the character budget tightens as the
// titles grow and they don't start colliding with the next panel.
//
// The x axis rather than the y because these titles run horizontally and
// compete for horizontal room. PrototypeDensity1D does the same thing off
// yAxisFontSize, where its facet labels are y-axis ticks.
const FACET_TITLE_SIZE_RATIO = 0.8;
const FALLBACK_FACET_TITLE_FONT_SIZE = 11;

const facetTitleFontSize = (xAxisFontSize: number) =>
  Number.isFinite(xAxisFontSize)
    ? Math.round(xAxisFontSize * FACET_TITLE_SIZE_RATIO)
    : // Mid-edit a font size field can be empty, and a NaN would propagate
      // into facetLabelBudget's arithmetic and out the other side as a
      // nonsense character budget.
      FALLBACK_FACET_TITLE_FONT_SIZE;

function SmallMultiplesScatter({
  data,
  xKey,
  yKey,
  xLabel,
  yLabel,
  facetKeys,
  facetOrder,
  height,
  legendForDownload,
  showBuiltinLegend = false,
  hoverTextKey,
  annotationTextKey,
  colorKey1,
  colorKey2,
  categoricalColorKey,
  continuousColorKey,
  contLegendKeys,
  colorMap,
  selectedPoints,
  pointVisibility,
  onClickPoint = () => {},
  onMultiselect = () => {},
  onClickResetSelection = () => {},
  onLoad = () => {},
  pointSize = 7,
  pointOpacity = 1,
  outlineWidth = 0.5,
  palette = DEFAULT_PALETTE,
  annotationFontSize = 12,
  xAxisFontSize = 12,
  yAxisFontSize = 12,
  pointsToAnnotate,
  selectionCount,
  showIdentityLine = false,
  regressionLinesByFacet,
  placeholderEmptyFacets = false,
  hiddenFacets,
  initialAxes = undefined,
  initialAnnotationTails = undefined,
  Plotly,
}: PropsWithPlotly) {
  const ref = useRef<(HTMLDivElement & ExtendedPlotType) | null>(null);
  usePlotResizer(Plotly, ref);

  // Dragmode is React state, not just plotly's internal layout, so every
  // Plotly.react re-applies the *current* mode. The hidden modebar means
  // setDragmode (below) is the only way it changes; driving it imperatively
  // would get clobbered the next time the effect re-reacts (e.g. after a
  // click updates the selection), flipping the mode back and desyncing the
  // custom toolbar. Matches PrototypeScatterPlot.
  const [dragmode, setDragmode] = useState<Layout["dragmode"]>("zoom");

  // Master axis ranges, preserved across re-reacts so that changing dragmode
  // (or selecting) doesn't snap the zoom back to full extent. Every non-
  // master axis uses `matches`, so only the master ("xaxis"/"yaxis") pair
  // carries a range and the rest follow it. Mirrors PrototypeScatterPlot.
  const axes = useRef<{
    xaxis?: { range?: number[]; autorange: boolean };
    yaxis?: { range?: number[]; autorange: boolean };
  }>(narrowSeededAxes(initialAxes));
  // Identity of the data currently on the axes. When it changes (a new x/y
  // dataset), the preserved zoom is dropped so the plot re-autoranges.
  //
  // Pre-seeded when we were handed a zoom to start from, or the first run of
  // the effect below would see a null dataset key, treat it as a change, and
  // discard that zoom before it was ever applied.
  const lastDataset = useRef<string | null>(
    initialAxes ? `${xLabel} ${yLabel}` : null
  );
  // User-dragged annotation tail offsets (ax/ay pixel offsets), keyed by point.
  // Mirrors the single-panel plots: restored onto the annotation objects each
  // render and re-captured from plotly_relayout, so a label the user repositions
  // stays put across selection/zoom/data changes. Pixel offsets are relative to
  // the anchor point, so they remain meaningful across zoom and facet layout.
  const annotationTails = useRef<Record<string, AnnotationTail>>(
    initialAnnotationTails ? { ...initialAnnotationTails } : {}
  );

  useEffect(() => {
    // Capture the node now; ref.current may have changed by the time this
    // cleanup runs (react-hooks/exhaustive-deps).
    const node = ref.current;
    return () => {
      if (node) {
        releaseWebglContexts(node as HTMLElement);
        Plotly.purge(node as HTMLElement);
      }
    };
  }, [Plotly]);

  useEffect(() => {
    const plot = ref.current;
    if (!plot) {
      return undefined;
    }

    // Reset the preserved zoom when an axis's dataset changes. The axis label
    // is the signal: coloring, faceting, selection, and dragmode don't change
    // it (so those keep the current zoom), but swapping the x/y dataset does.
    const datasetKey = `${xLabel}\u0000${yLabel}`;
    if (lastDataset.current !== datasetKey) {
      axes.current = {};
      lastDataset.current = datasetKey;
    }

    const x = data[xKey] as (number | null)[];
    const y = data[yKey] as (number | null)[];
    const text = hoverTextKey
      ? (data[hoverTextKey] as string[])
      : x.map(() => "");
    const annotationText = annotationTextKey
      ? (data[annotationTextKey] as string[])
      : x.map((_, i) => `${x[i]?.toFixed(2)}, ${y[i]?.toFixed(2)}`);
    const visible = pointVisibility ?? x.map(() => true);

    const color1 = colorKey1 ? (data[colorKey1] as (boolean | null)[]) : null;
    const color2 = colorKey2 ? (data[colorKey2] as (boolean | null)[]) : null;
    const catColorData = categoricalColorKey
      ? (data[categoricalColorKey] as (string | number | null)[])
      : null;
    const contColorData = continuousColorKey
      ? (data[continuousColorKey] as (number | null)[])
      : null;

    const selected = selectedPoints ?? new Set<number>();
    const selectedpoints = [...selected];

    // Annotation channel (expansion-selection): label the contacted points, not
    // the re-expanded selection. They coincide off the facet_by === "expansion"
    // collapse; the wrapper passes `pointsToAnnotate` only on the faceted
    // (collapse) path, so the fallback keeps any other caller unchanged.
    const pointsForAnnotation = pointsToAnnotate ?? selected;
    // Count for the over-cap fallback. selectionCount is the model count under
    // collapse (selection.size). TODO: the noun "points" should become the index
    // type's display name once that is threaded through; literal for now.
    const annotationCount = selectionCount ?? selected.size;

    // Candidate facets, then drop any with no plottable point at all (every
    // point null on an axis). This is the legend's non-plottable-entity rule
    // applied to facet selection: a facet that can never render shouldn't
    // hold a slot. Deliberately ignores visibility — a facet hidden only by a
    // color-legend toggle still has plottable data, so it's kept (and renders
    // empty). A facet hidden via the "Facets" panel (hiddenFacets, below) is
    // different: that's a deliberate, causal user action, so it's dropped
    // outright rather than rendered as an empty placeholder.
    const candidateFacets = (
      facetOrder ?? Array.from(new Set(facetKeys))
    ).filter((f) => !hiddenFacets?.has(f));
    const plottableFacets = new Set<string>();
    const visibleFacets = new Set<string>();
    for (let i = 0; i < x.length; i += 1) {
      if (x[i] !== null && y[i] !== null) {
        plottableFacets.add(facetKeys[i]);
        if (visible[i]) {
          visibleFacets.add(facetKeys[i]);
        }
      }
    }
    const facets = placeholderEmptyFacets
      ? candidateFacets
      : candidateFacets.filter((f) => plottableFacets.has(f));
    const F = facets.length;
    const cols = Math.max(1, Math.ceil(Math.sqrt(F)));
    const rows = Math.max(1, Math.ceil(F / cols));

    // Shared extents across all points (mirrors PrototypeScatterPlot). Every
    // panel shares one range via `matches`, so the identity line and the
    // autoscale hack are computed once from these.
    const [minX, maxX] = getRange(x as number[]);
    const [minY, maxY] = getRange(y as number[]);
    const rangeX = maxX - minX;
    const rangeY = maxY - minY;
    const ratio = Math.min(rangeX, rangeY) / Math.max(rangeX, rangeY);
    const extents = { minX, maxX, minY, maxY, rangeX, rangeY, ratio };

    const resolvedHeight =
      typeof height === "number"
        ? height
        : plot.clientHeight || plot.parentElement?.clientHeight || 600;

    const facetTitleSize = facetTitleFontSize(xAxisFontSize);

    // The gap between panels is a fixed fraction of the whole grid, but
    // what actually has to fit inside it doesn't stay fixed: gapX holds
    // each row's y-axis tick numbers (sized off yAxisFontSize, drawn in
    // the horizontal gap between columns), and gapY holds both the x-axis
    // tick numbers below a panel and the facet title above the next one
    // (both sized off xAxisFontSize — facetTitleSize is a fixed ratio of
    // it). Left as constants, a bigger font just encroaches further into
    // the neighboring panel with nothing to stop it. Scaled by the same
    // current-vs-tuned-for ratio used throughout this file, so today's
    // spacing is exactly preserved at the live plot's own default.
    const gapX = DEFAULT_GAP_X * (yAxisFontSize / DEFAULT_GAP_AXIS_FONT_SIZE);
    const gapY = DEFAULT_GAP_Y * (xAxisFontSize / DEFAULT_GAP_AXIS_FONT_SIZE);

    // MARGIN is a floor sized for a single line each. The shared axis
    // labels below are paper-anchored annotations, not native titles, so
    // nothing grows this on its own for a second or third `<br>`-separated
    // line the way automargin would for a real title (see
    // calcExtraLinesMargin) — a multi-line label needs it added by hand,
    // or it just clips or overlaps the plot. `yLabel` is rotated -90°, so
    // its extra lines run horizontally once rotated and grow `l`, not `b`.
    const resolvedMargin = {
      ...MARGIN,
      b: MARGIN.b + calcExtraLinesMargin(xLabel, xAxisFontSize),
      l: MARGIN.l + calcExtraLinesMargin(yLabel, yAxisFontSize),
    };

    // No `width` prop to consult — plotly autosizes to the container — so this
    // is measured, with 0 standing for "not measured yet" (facetLabelBudget
    // treats that as "don't tighten").
    const resolvedWidth =
      plot.clientWidth || plot.parentElement?.clientWidth || 0;

    // The widest a facet title may run before it starts colliding with the one
    // over the next panel. Falls with `cols`, which is how it falls with the
    // facet count.
    const labelBudget = facetLabelBudget({
      gridWidth: resolvedWidth
        ? resolvedWidth - resolvedMargin.l - resolvedMargin.r
        : 0,
      cols,
      fontSize: facetTitleSize,
    });

    const layout: Record<string, any> = {
      height: resolvedHeight,
      margin: resolvedMargin,
      // Every real trace below sets `showlegend: false` explicitly, so this
      // has no effect unless showBuiltinLegend adds its dummy, named traces
      // (which force `showlegend: true`). Mirrors PrototypeScatterPlot.
      showlegend: true,
      hovermode: "closest",
      dragmode,
      annotations: [] as any[],
      // Master-only autoscale hack: equalizes x/y scale so the identity line is
      // ~45°, propagating to matched panels. Returns [] when showIdentityLine is
      // off, so default behavior is unchanged. The visible indicator shapes are
      // injected post-autorange (see plotly_afterplot).
      shapes: calcAutoscaleShapes(showIdentityLine, extents),
    };

    const plotlyData: Partial<PlotData>[] = [];

    // Color group membership is facet-independent, but the order they are
    // painted in is not, and that order is what decides which points get
    // buried. The rule is "smallest group on top", and the size that matters is
    // the size *in this panel* — a color that is rare overall can dominate one
    // facet, and a color that is common overall can be the handful of points
    // someone is looking for in another. Facets partition the points, so the
    // per-facet count is exactly well defined.
    //
    // Restricting `visible` to the facet is what does it: the seam already
    // derives its counts, its ordering, and its "drop groups with no visible
    // points" rule from that mask. That last one now drops colors absent from
    // this panel rather than only colors absent everywhere, so the trace count
    // falls out of F×G to the sum of what each panel actually contains —
    // which more than pays for resolving the seam per facet instead of once.
    const solidGroupsFor = (facet: string) => {
      if (contColorData) {
        return null;
      }

      const inFacet = facetMaskFor(facetKeys, facet, x, y, visible);

      return getSolidColorGroups({
        color1,
        color2,
        catColorData,
        colorMap,
        palette,
        visible: visible.map((_, i) => inFacet(i)),
        // Counts tie whenever color is an expansion member — every member has
        // one point per index entity — so the order needs something else to go
        // on. See orderColorKeysByCount.
        axes: [x, y],
      });
    };

    // The mask shared by every trace: a point is plottable in facet `facet`
    // only if it's visible, in that facet, and has values on both axes.
    const maskFor = (facet: string, member: (i: number) => boolean) => {
      const inFacet = facetMaskFor(facetKeys, facet, x, y, visible);
      return (i: number) => (inFacet(i) ? member(i) : false);
    };

    // Maps a continuous trace's curveNumber to its bin-stacked draw order, so
    // click/select can map a reordered pointIndex back to the original index.
    // Only continuous traces are reordered; every other trace stays in natural
    // (global-index) order, so they're simply absent from this map.
    const contOrderByCurve = new Map<number, number[]>();

    facets.forEach((facet, k) => {
      const col = k % cols;
      const row = Math.floor(k / cols);

      const xDomain: [number, number] = [
        col / cols + (col === 0 ? 0 : gapX / 2),
        (col + 1) / cols - (col === cols - 1 ? 0 : gapX / 2),
      ];
      const yTop = 1 - row / rows;
      const yBot = 1 - (row + 1) / rows;
      const yDomain: [number, number] = [yBot + gapY / 2, yTop - gapY / 2];

      const suffix = k === 0 ? "" : String(k + 1);
      const xRef = `x${suffix}`;
      const yRef = `y${suffix}`;

      // A selection lives in exactly one facet, so only that panel should dim
      // its unselected points; the others stay at full opacity.
      const facetHasSelection = selectedpoints.some(
        (i) => facetKeys[i] === facet
      );

      // A facet kept by the plottable check above but with no currently
      // visible point is hidden by a legend toggle (e.g. coloring AND
      // faceting by expansion, then toggling a transcript off). Keep its
      // slot so the grid stays stable, but mark it so the empty frame reads
      // as intentional rather than a rendering bug. (A facet with no
      // plottable data at all was already dropped, so this is toggle-only.)
      const facetNoData = !plottableFacets.has(facet);
      const facetHidden = !facetNoData && !visibleFacets.has(facet);

      layout[`xaxis${suffix}`] = {
        domain: xDomain,
        anchor: yRef,
        matches: k === 0 ? undefined : "x",
        tickfont: { size: Math.max(8, xAxisFontSize - 3) },
        exponentformat: "e",
        zeroline: false,
        // Only the master carries a range; matched axes follow it.
        ...(k === 0 ? axes.current.xaxis ?? { autorange: true } : {}),
      };
      layout[`yaxis${suffix}`] = {
        domain: yDomain,
        anchor: xRef,
        matches: k === 0 ? undefined : "y",
        tickfont: { size: Math.max(8, yAxisFontSize - 3) },
        exponentformat: "e",
        zeroline: false,
        ...(k === 0 ? axes.current.yaxis ?? { autorange: true } : {}),
      };

      // Bottom of the stack: the selection-outline trace (a larger, dark-ringed
      // transparent marker that peeks out from behind selected points).
      const outlineMask = maskFor(facet, () => true);
      plotlyData.push({
        type: "scattergl",
        mode: "markers",
        xaxis: xRef,
        yaxis: yRef,
        x: x.map((v, i) => (outlineMask(i) ? v : null)),
        y: y.map((v, i) => (outlineMask(i) ? v : null)),
        name: "",
        hoverinfo: "skip",
        showlegend: false,
        selectedpoints,
        marker: {
          color: "transparent",
          size: pointSize + outlineWidth * 2,
          line: { color: "#000", width: Math.max(outlineWidth, 2) },
        },
        selected: { marker: { opacity: 1 } },
        unselected: { marker: { opacity: 0 } },
      } as Partial<PlotData>);

      const pushSolid = (color: string, member: (i: number) => boolean) => {
        const mask = maskFor(facet, member);
        plotlyData.push({
          type: "scattergl",
          mode: "markers",
          xaxis: xRef,
          yaxis: yRef,
          x: x.map((v, i) => (mask(i) ? v : null)),
          y: y.map((v, i) => (mask(i) ? v : null)),
          text,
          name: "",
          hoverinfo: "x+y+text",
          hoverlabel: { bgcolor: color },
          showlegend: false,
          selectedpoints,
          marker: {
            color: hexToRgba(color, pointOpacity),
            size: pointSize,
            line: { color, width: outlineWidth },
          },
          selected: { marker: { opacity: 1 } },
          unselected: { marker: { opacity: facetHasSelection ? 0.5 : 1 } },
        } as Partial<PlotData>);
      };

      if (contColorData) {
        // Continuous: the "other" (null-valued) points first (bottom), then one
        // colorscale trace whose points are ordered so this facet's
        // smallest-population bins draw last (on top), matching
        // PrototypeScatterPlot. The order is computed from this facet's visible
        // points (outlineMask) so each panel stacks by its own populations.
        // Because the points are reordered, we record the order under the
        // trace's curveNumber so click/select can map back to original indices.
        pushSolid(palette.other, (i) => contColorData[i] === null);

        const contMask = maskFor(facet, (i) => contColorData[i] !== null);
        // Count basis must match PrototypeScatterPlot, which counts plain
        // `visible` with no plottability check. facetMaskFor (via outlineMask)
        // also requires x/y non-null, which would drop visible-but-unplottable
        // points from the per-facet bin counts and can flip the stacking order
        // wherever two bins have near-equal populations.
        const facetVisible = x.map(
          (_, i) => visible[i] && facetKeys[i] === facet
        );
        const order =
          contLegendKeys && colorMap
            ? orderContinuousPointsByBin(
                contColorData,
                contLegendKeys,
                colorMap,
                facetVisible
              )
            : x.map((_, i) => i);

        contOrderByCurve.set(plotlyData.length, order);
        plotlyData.push({
          type: "scattergl",
          mode: "markers",
          xaxis: xRef,
          yaxis: yRef,
          x: order.map((i) => (contMask(i) ? x[i] : null)),
          y: order.map((i) => (contMask(i) ? y[i] : null)),
          text: text ? order.map((i) => text[i]) : text,
          name: "",
          hoverinfo: "x+y+text",
          showlegend: false,
          selectedpoints: order.reduce((acc: number[], origIndex, i) => {
            if (selected.has(origIndex)) {
              acc.push(i);
            }
            return acc;
          }, []),
          // Per-point hover background keyed to each point's bin color (in the
          // reordered draw order), matching PrototypeScatterPlot. Without this
          // the continuous trace's hover box falls back to Plotly's default
          // black; only the separate "other" trace (via pushSolid) was colored.
          hoverlabel: {
            bgcolor: order.map(
              (i) =>
                (contLegendKeys
                  ? colorMap.get(contLegendKeys[i])
                  : undefined) ?? palette.other
            ) as any,
          },
          marker: {
            size: pointSize,
            color: order.map((i) => contColorData[i] ?? "transparent"),
            colorscale: palette.sequentialScale.map(([value, color]) => [
              value,
              hexToRgba(color as string, pointOpacity),
            ]),
            line: {
              width: outlineWidth,
              color: order.map((i) => contColorData[i] ?? "transparent"),
              colorscale: palette.sequentialScale,
            },
          },
          selected: { marker: { opacity: 1 } },
          unselected: { marker: { opacity: facetHasSelection ? 0.5 : 1 } },
        } as Partial<PlotData>);
      } else {
        // Solid color groups (none / categorical / comparison), in this
        // panel's paint order.
        (solidGroupsFor(facet) ?? []).forEach((group) =>
          pushSolid(group.color, group.includes)
        );
      }

      // Facet title (paper-positioned, centered above the panel). Trimmed the
      // same way the density plot trims its track labels — a name wider than
      // its panel overlaps the neighboring title rather than being clipped,
      // since these are paper-positioned annotations with nothing to clip them.
      // Unlike the density plot, the budget is the grid's, not a constant.
      (layout.annotations as any[]).push({
        text: truncateFacetLabel(facet, labelBudget),
        x: (xDomain[0] + xDomain[1]) / 2,
        y: yTop - gapY / 2 + 0.012,
        xref: "paper",
        yref: "paper",
        xanchor: "center",
        yanchor: "bottom",
        showarrow: false,
        font: {
          size: facetTitleSize,
          color: facetHidden || facetNoData ? "#999" : undefined,
        },
      });

      // No data for this facet in the chosen dataset: an inert "(no data)"
      // placeholder over the empty panel, so the page keeps its full set of
      // panels and the gap is explained rather than silently missing.
      if (facetNoData) {
        (layout.annotations as any[]).push({
          text: "(no data)",
          x: (xDomain[0] + xDomain[1]) / 2,
          y: (yDomain[0] + yDomain[1]) / 2,
          xref: "paper",
          yref: "paper",
          xanchor: "center",
          yanchor: "middle",
          showarrow: false,
          font: { size: facetTitleSize, color: "#999" },
        });
      }

      // Toggled-off facet: an inert placeholder over the (empty) panel.
      if (facetHidden) {
        (layout.annotations as any[]).push({
          text: "(hidden)",
          x: (xDomain[0] + xDomain[1]) / 2,
          y: (yDomain[0] + yDomain[1]) / 2,
          xref: "paper",
          yref: "paper",
          xanchor: "center",
          yanchor: "middle",
          showarrow: false,
          font: { size: facetTitleSize, color: "#999" },
        });
      }
    });

    if (showBuiltinLegend) {
      const legendTemplateTrace = {
        marker: { size: pointSize, line: { width: outlineWidth } },
      };
      getLegendTraces(legendForDownload!, legendTemplateTrace).forEach((t) =>
        plotlyData.push(t as typeof plotlyData[number])
      );
    }

    // Selected-point annotations, placed on their own facet's axes. Above the
    // per-facet cap we fall back to a single count, mirroring the single-panel
    // plot.
    // One shared title per axis, centered along the grid's bottom and left
    // edges, instead of repeating a title on every bottom/left subplot. The
    // shifts push each into its margin; tune with the margins above.
    (layout.annotations as any[]).push(
      {
        text: xLabel,
        x: 0.5,
        y: 0,
        xref: "paper",
        yref: "paper",
        yshift: -44,
        xanchor: "center",
        yanchor: "bottom",
        showarrow: false,
        font: { size: xAxisFontSize },
      },
      {
        text: yLabel,
        x: 0,
        y: 0.5,
        xref: "paper",
        yref: "paper",
        xshift: -54,
        xanchor: "center",
        yanchor: "middle",
        textangle: -90,
        showarrow: false,
        font: { size: yAxisFontSize },
      }
    );

    const facetIndex = new Map(facets.map((f, i) => [f, i]));
    if (
      pointsForAnnotation.size > 0 &&
      pointsForAnnotation.size <= MAX_POINTS_TO_ANNOTATE
    ) {
      [...pointsForAnnotation].forEach((pointIndex) => {
        if (
          typeof x[pointIndex] !== "number" ||
          typeof y[pointIndex] !== "number"
        ) {
          return;
        }
        const k = facetIndex.get(facetKeys[pointIndex]);
        if (k === undefined) {
          return;
        }
        // Place the label on its own facet's axes (each subplot has its own
        // x{suffix}/y{suffix}); suffix "" is the first panel's x/y.
        const suffix = k === 0 ? "" : String(k + 1);
        // Restore any annotation arrowhead position the user may have
        // edited, rescaled for the current annotationFontSize — see
        // resolveAnnotationTail.
        const tail = resolveAnnotationTail(
          annotationTails.current[`${xKey}-${yKey}-${pointIndex}`],
          annotationFontSize
        );

        (layout.annotations as any[]).push({
          x: x[pointIndex],
          y: y[pointIndex],
          text: annotationText[pointIndex],
          visible: visible[pointIndex],
          xref: `x${suffix}`,
          yref: `y${suffix}`,
          arrowhead: 0,
          arrowwidth: calcAnnotationArrowWidth(annotationFontSize),
          standoff: 4,
          font: { size: annotationFontSize },
          arrowcolor: "#888",
          bordercolor: "#c7c7c7",
          bgcolor: "#fff",
          // Identifies this label for the relayout capture below; also the key
          // for restoring a tail the user dragged. The facet titles and axis
          // labels in this same array carry no pointIndex (and no tail).
          pointIndex,
          ax: tail?.ax,
          ay: tail?.ay,
        });
      });
    } else if (pointsForAnnotation.size > MAX_POINTS_TO_ANNOTATE) {
      (layout.annotations as any[]).push({
        text: `(${annotationCount} selected points)`,
        font: { size: annotationFontSize },
        xref: "paper",
        yref: "paper",
        x: 1,
        y: 1,
        showarrow: false,
        bordercolor: "#c7c7c7",
        bgcolor: "#fff",
      });
    }

    // Pre-seeds non-overlapping tail offsets, mirroring PrototypeScatterPlot's
    // identically-named helper. calcAnnotationPositions itself is single-axis
    // (it just calls whatever axis object's l2p it's handed) — grouping points
    // by facet and calling it once per facet with that facet's own
    // xaxis{suffix}/yaxis{suffix} object is a direct reuse, not a hack.
    const assignAnnotationPositions = (pointIndices: number[]) => {
      if (new Set(pointIndices).size > MAX_POINTS_TO_ANNOTATE) {
        return;
      }

      const fullLayout = (plot as any)._fullLayout;

      const byFacet = new Map<number, number[]>();
      pointIndices.forEach((pointIndex) => {
        const k = facetIndex.get(facetKeys[pointIndex]);
        if (k === undefined) {
          return;
        }
        if (!byFacet.has(k)) {
          byFacet.set(k, []);
        }
        byFacet.get(k)!.push(pointIndex);
      });

      byFacet.forEach((indices, k) => {
        const suffix = k === 0 ? "" : String(k + 1);
        const facetLayout = {
          xaxis: fullLayout[`xaxis${suffix}`],
          yaxis: fullLayout[`yaxis${suffix}`],
        };

        calcAnnotationPositions(
          x as number[],
          y as number[],
          indices,
          facetLayout
        ).forEach(({ pointIndex, ax, ay }) => {
          annotationTails.current[
            `${xKey}-${yKey}-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        });
      });
    };

    const config: Partial<Config> = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: ["lasso2d"],
      // Let the user drag a label's tail (but not the label body), same as the
      // single-panel plots.
      edits: { annotationTail: true },
    };

    Plotly.react(plot, plotlyData, layout as Partial<Layout>, config);

    // Capture the master ranges Plotly computed on first render, so later
    // re-reacts restore them rather than re-autoranging.
    if (!axes.current.xaxis || !axes.current.yaxis) {
      axes.current = {
        xaxis: { range: (plot.layout as any).xaxis?.range, autorange: false },
        yaxis: { range: (plot.layout as any).yaxis?.range, autorange: false },
      };
    }

    // Convenience methods the plot controls call on the figure node (the
    // modebar is hidden, so these are the only way to drive it). It's one
    // figure with `matches`-shared axes, so dragmode/zoom act on the whole
    // grid at once.
    const getButton = (attr: string, val: string) =>
      plot.querySelector(
        `.modebar-btn[data-attr="${attr}"][data-val="${val}"]`
      ) as HTMLAnchorElement | null;
    const zoom = (val: "in" | "out" | "reset") =>
      getButton("zoom", val)?.click();

    plot.setDragmode = (nextDragmode) => {
      const resetting =
        dragmode !== nextDragmode &&
        (nextDragmode === "select" || nextDragmode === "lasso");
      if (resetting && onClickResetSelection) {
        onClickResetSelection();
      }
      setDragmode(nextDragmode);
    };
    plot.resetZoom = () => zoom("reset");
    plot.zoomIn = () => zoom("in");
    plot.zoomOut = () => zoom("out");
    plot.getImageFigure = (options) => {
      if (!legendForDownload) {
        window.console.warn("`legendForDownload` is undefined");
        return null;
      }

      // Position comes from the export config, not from a plot style — see
      // legendLayoutFor. Called with no options (the SVG path, or any other
      // caller) it keeps today's right-hand placement.
      const legendPosition = options?.legendPosition ?? "right";
      const edgePadding = options?.edgePadding ?? EXPORT_EDGE_PADDING;
      const chromeLineWidth =
        options?.chromeLineWidth ?? DEFAULT_CHROME_LINE_WIDTH;
      const dataLineWidth = options?.dataLineWidth ?? DEFAULT_DATA_LINE_WIDTH;
      const xAxisLabelOverride = options?.xAxisLabel;
      const yAxisLabelOverride = options?.yAxisLabel;
      const exportLegend = applyLegendLabelOverrides(
        legendForDownload,
        options?.legendTitle,
        options?.legendItemLabels
      );

      const legendTemplateTrace = {
        marker: { size: pointSize, line: { width: outlineWidth } },
      };
      const legendTraces = wantsLegendTraces(legendPosition)
        ? getLegendTraces(exportLegend, legendTemplateTrace)
        : [];

      // Recompute per-facet shapes with simulateInfiteLength=false (bounded
      // endpoints for a static image) — mirrors the indicatorShapes loop
      // above exactly, but plot.layout.shapes holds the invisible autoscale
      // decoy by this point (see the plotly_afterplot handler below), not
      // the real lines, so a fresh computation is required. Same rationale
      // as PrototypeScatterPlot's own downloadImage.
      //
      // See widenExtentsToAxisRange for why bounded still needs to reach
      // the axis range, not just the data's own extents. One shared range
      // for every facet: every facet's axis `matches` the master (k === 0),
      // so they all display the same range regardless of suffix.
      const exportExtents = widenExtentsToAxisRange(
        extents,
        (plot.layout as any).xaxis?.range,
        (plot.layout as any).yaxis?.range
      );
      const downloadShapes: NonNullable<Layout["shapes"]> = [];
      // Each facet has its own axis pair (xaxis/yaxis, xaxis2/yaxis2, …), so
      // chrome line width — unlike edgePadding's single shared MARGIN — has
      // to be applied per facet rather than once.
      const chromeAxisOverrides: Record<string, unknown> = {};

      for (let k = 0; k < facets.length; k += 1) {
        const suffix = k === 0 ? "" : String(k + 1);

        if (showIdentityLine || regressionLinesByFacet) {
          const facetLines = regressionLinesByFacet?.get(facets[k]) ?? null;
          downloadShapes.push(
            ...(calcPlotIndicatorLineShapes(
              showIdentityLine,
              facetLines,
              exportExtents,
              false,
              { xref: `x${suffix}`, yref: `y${suffix}` },
              dataLineWidth
            ) ?? [])
          );
        }

        const layoutBag = (plot.layout as unknown) as Record<
          string,
          Record<string, unknown> | undefined
        >;

        chromeAxisOverrides[`xaxis${suffix}`] = {
          ...layoutBag[`xaxis${suffix}`],
          ...calcChromeAxisOverrides(chromeLineWidth),
        };
        chromeAxisOverrides[`yaxis${suffix}`] = {
          ...layoutBag[`yaxis${suffix}`],
          ...calcChromeAxisOverrides(chromeLineWidth),
        };
      }

      const figure = cloneFigureForExport([...plot.data, ...legendTraces], {
        ...plot.layout,
        ...chromeAxisOverrides,
        // Both axis-label annotations (below) sit a fixed pixel shift into
        // MARGIN — no `automargin` here to lean on (MARGIN is static), so
        // the floor has to grow by the same amount the shift below grows
        // by, or the label just moves deeper into a margin that never got
        // any bigger and starts crowding the true edge instead of gaining
        // room. This is the *inner* half — see the shift adjustment below,
        // and see PrototypeScatterPlot's identical l/b-only formula for
        // its automargin-based equivalent.
        //
        // Starts from the same calcExtraLinesMargin floor the live render
        // uses (see resolvedMargin above) — but recomputed here rather than
        // reused, because the export's own label editor can override the
        // text to something with a different line count than whatever the
        // live plot is currently showing.
        margin: {
          ...MARGIN,
          l:
            MARGIN.l +
            calcExtraLinesMargin(yAxisLabelOverride ?? yLabel, yAxisFontSize) +
            edgePadding,
          b:
            MARGIN.b +
            calcExtraLinesMargin(xAxisLabelOverride ?? xLabel, xAxisFontSize) +
            edgePadding,
        },
        shapes: downloadShapes,
        // See PrototypeScatterPlot's note: the font follows the axis font size
        // so the legend scales with everything else in the figure.
        ...legendLayoutFor(legendPosition, {
          title: exportLegend.title,
          fontSize: xAxisFontSize,
        }),
      });

      // The two shared axis-label annotations (pushed once, above — not
      // per-facet, and not any of the facet titles/placeholders, which
      // never set a shift at all). Growing their shift by edgePadding is
      // the inner half: the label moves further from the tick numbers, by
      // exactly the same amount the margin above just grew, so its
      // absolute position holds steady while the tick numbers (which do
      // track the margin) move away from it — see the analogous
      // standoff/margin pairing in PrototypeScatterPlot for why growing
      // both by the same amount is what actually opens up the gap rather
      // than just relocating it.
      if (figure) {
        figure.layout.annotations = (
          (figure.layout.annotations as any[]) ?? []
        ).map((a) => {
          // Same discriminator as the shift adjustment: the x-axis label is
          // the only annotation with a yshift, the y-axis label the only
          // one with an xshift. `yanchor`/`xanchor` on these is the far
          // edge from the plot ("bottom" / vertically centered with
          // rotated text growing outward) — the label grows *away* from
          // the anchor, toward the plot, so without also pushing the
          // anchor further out by the same extra-lines amount the margin
          // just grew by, a second or third line would grow straight into
          // the tick numbers instead of into the room just reserved for it.
          if ("yshift" in a) {
            const labelText = xAxisLabelOverride ?? xLabel;

            return {
              ...a,
              yshift:
                a.yshift -
                edgePadding -
                calcExtraLinesMargin(labelText, xAxisFontSize),
              ...(xAxisLabelOverride !== undefined ? { text: labelText } : {}),
            };
          }

          if ("xshift" in a) {
            const labelText = yAxisLabelOverride ?? yLabel;

            return {
              ...a,
              xshift:
                a.xshift -
                edgePadding -
                calcExtraLinesMargin(labelText, yAxisFontSize),
              ...(yAxisLabelOverride !== undefined ? { text: labelText } : {}),
            };
          }

          return a;
        });

        // A label dragged in ExportImageModal's own live "Adjust label
        // positions" preview — applied after the yshift/xshift pass above
        // so it lands on that pass's own output rather than being bypassed
        // by it, and always wins over whatever ax/ay the live plot's own
        // stored tail resolved to.
        figure.layout.annotations = applyAnnotationTailOverrides(
          figure.layout.annotations as any[] | undefined,
          options?.annotationTailOverrides,
          annotationFontSize
        );
      }

      return figure;
    };

    plot.downloadImage = (options) => {
      const figure = plot.getImageFigure();

      if (figure) {
        Plotly.downloadImage(figure, options as any);
      }
    };
    // Faceted: navigating-to-a-point is still off (a safe stub). Per-point
    // annotations are on and their tails ARE draggable (see config.edits and the
    // plotly_relayout capture). `annotateSelected` pre-seeds non-overlapping
    // tail offsets via assignAnnotationPositions (see above), same as
    // PrototypeScatterPlot's identical handle — the context path's call now
    // does real work here too, not just a no-op.
    plot.isPointInView = () => true;
    plot.xValueMissing = (pointIndex: number) => x[pointIndex] === null;
    plot.yValueMissing = (pointIndex: number) => y[pointIndex] === null;
    plot.annotateSelected = (points?: number[]) => {
      const pts = points ?? (selectedPoints ? [...selectedPoints] : []);
      assignAnnotationPositions(pts);
    };
    plot.removeAnnotations = () => {};

    if (onLoad) {
      onLoad(plot);
    }

    // Per-facet indicator shapes: the identity line (same in every panel) plus
    // this panel's regression line(s), if any were fit (regressionLinesByFacet
    // — one per panel normally, one per color under the per-color option).
    // The shared `extents` is identical across panels (ranges linked by
    // `matches`); only the subplot refs and per-facet slope/intercept differ.
    // Built here, injected after autorange settles (below).
    const indicatorShapes: NonNullable<Layout["shapes"]> = [];
    if (showIdentityLine || regressionLinesByFacet) {
      for (let k = 0; k < facets.length; k += 1) {
        const suffix = k === 0 ? "" : String(k + 1);
        const facetLines = regressionLinesByFacet?.get(facets[k]) ?? null;
        indicatorShapes.push(
          ...(calcPlotIndicatorLineShapes(
            showIdentityLine,
            facetLines,
            extents,
            true,
            { xref: `x${suffix}`, yref: `y${suffix}` }
          ) ?? [])
        );
      }
    }

    const listeners: [string, (e: any) => void][] = [];
    const on = (name: string, cb: (e: any) => void) => {
      listeners.push([name, cb]);
      plot.on(
        name as Parameters<PlotlyHTMLElement["on"]>[0],
        cb as Parameters<PlotlyHTMLElement["on"]>[1]
      );
    };

    // Re-inject indicator shapes AFTER autorange has settled, so their
    // over-length endpoints can't perturb the computed ranges. We then reset
    // the layout's base shapes to the autoscale hack so it — not the
    // over-length lines — feeds any later autorange.
    //
    // This must run on EVERY afterplot, not just the first: a later redraw
    // (notably a window resize, which usePlotResizer services via
    // Plotly.Plots.resize) repaints from those indicator-less base shapes and
    // would otherwise drop the identity/regression lines. Re-asserting here
    // puts them back; Plotly de-dupes the no-op repeats when nothing changed,
    // so it doesn't loop. Mirrors PrototypeScatterPlot's handling.
    // The legend is drawn from dummy traces (getLegendTraces) that hold no
    // real points, so Plotly's default click behavior toggles the dummy and
    // nothing else: the entry greys out while the plot stays put. Returning
    // false suppresses that, leaving the legend a static key rather than a
    // control that appears to do something. Registered unconditionally — with
    // no legend shown (Data Explorer's case) these never fire.
    // TODO: make these actually filter, as Data Explorer's own legend does.
    on("plotly_legendclick", () => false);
    on("plotly_legenddoubleclick", () => false);

    // https://github.com/plotly/plotly.js/blob/55dda47/src/lib/prepare_regl.js
    // Mirrors PrototypeScatterPlot's handling; this renderer didn't have it.
    on("plotly_webglcontextlost", () => {
      Plotly.redraw(plot);
    });

    // See onWebglContextRestored: complements the handler above for the
    // case where the context is lost to eviction rather than idling —
    // there, nothing is drawable again until *this* fires.
    const stopWatchingContextRestore = onWebglContextRestored(plot, () => {
      Plotly.redraw(plot);
    });

    on("plotly_afterplot", () => {
      if (
        indicatorShapes.length === 0 ||
        (plot.layout as any).shapes === indicatorShapes
      ) {
        return;
      }
      Plotly.update(plot, {}, { shapes: indicatorShapes });
      (plot.layout as any).shapes = calcAutoscaleShapes(
        showIdentityLine,
        extents
      );
    });

    on("plotly_click", (e: PlotMouseEvent) => {
      const pt = e.points[0];
      if (!pt) {
        return;
      }
      const native = e.event as MouseEvent;
      const anyModifier = native.ctrlKey || native.metaKey || native.shiftKey;
      const order = contOrderByCurve.get(pt.curveNumber as number);
      const index = order
        ? order[pt.pointIndex as number]
        : (pt.pointIndex as number);
      onClickPoint(index, anyModifier);
    });

    on("plotly_selecting", () => {
      if (selected.size > 0) {
        onClickResetSelection();
      }
    });

    on("plotly_selected", (e: PlotSelectionEvent) => {
      const points = (e?.points ?? [])
        .filter(
          (p) => p.data.hoverinfo !== "skip" && p.data.hoverinfo !== "none"
        )
        .map((p) => {
          const order = contOrderByCurve.get(p.curveNumber as number);
          return order
            ? order[p.pointIndex as number]
            : (p.pointIndex as number);
        });
      if (points.length > 0) {
        assignAnnotationPositions(points);
        onMultiselect(points);
      }
    });

    on("plotly_deselect", () => {
      onClickResetSelection();
    });

    on("plotly_relayout", () => {
      axes.current = {
        xaxis: { range: (plot.layout as any).xaxis?.range, autorange: false },
        yaxis: { range: (plot.layout as any).yaxis?.range, autorange: false },
      };

      // Persist any annotation tail the user just dragged. The annotations array
      // also holds non-draggable titles/axis labels (showarrow:false, no tail,
      // no pointIndex); the guard skips them and captures only the per-point
      // labels. Keyed by pointIndex because the per-point labels are interspersed
      // with titles, so array position isn't a stable identifier.
      plot.layout.annotations?.forEach((annotation) => {
        const { ax, ay, pointIndex } = annotation as any;
        if (ax != null && ay != null && pointIndex != null) {
          annotationTails.current[
            `${xKey}-${yKey}-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        }
      });
    });

    return () => {
      listeners.forEach(([name, cb]) =>
        // Plotly's HTMLElement type omits removeListener, but the graph div is
        // an EventEmitter at runtime, so the method is present. Augment locally
        // rather than reaching for Parameters<PlotlyHTMLElement["removeListener"]>,
        // which can't index a property the type doesn't declare.
        (plot as PlotlyHTMLElement & {
          removeListener?: (
            event: string,
            callback: (...args: unknown[]) => void
          ) => void;
        }).removeListener?.(name, cb)
      );
      stopWatchingContextRestore();
    };
  }, [
    data,
    xKey,
    yKey,
    xLabel,
    yLabel,
    facetKeys,
    facetOrder,
    height,
    legendForDownload,
    showBuiltinLegend,
    hoverTextKey,
    annotationTextKey,
    colorKey1,
    colorKey2,
    categoricalColorKey,
    continuousColorKey,
    contLegendKeys,
    colorMap,
    selectedPoints,
    pointVisibility,
    onClickPoint,
    onMultiselect,
    onClickResetSelection,
    pointSize,
    pointOpacity,
    outlineWidth,
    palette,
    annotationFontSize,
    xAxisFontSize,
    yAxisFontSize,
    pointsToAnnotate,
    selectionCount,
    showIdentityLine,
    regressionLinesByFacet,
    onLoad,
    dragmode,
    placeholderEmptyFacets,
    hiddenFacets,
    Plotly,
  ]);

  return <div className={styles.ScatterPlot} ref={ref as any} />;
}

export default function LazySmallMultiplesScatter({
  data,
  ...otherProps
}: Props) {
  const PlotlyLoader = usePlotlyLoader();

  return (
    <PlotlyLoader version="module">
      {(Plotly: PlotlyType) =>
        data ? (
          <SmallMultiplesScatter data={data} Plotly={Plotly} {...otherProps} />
        ) : null
      }
    </PlotlyLoader>
  );
}
